import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  Select,
  MenuItem
} from '@mui/material';
import {
  setAppId,
  selectedApplicationData,
  setSelectedApplicationIdData,
  addBreadcrumb,
  setAppetiteData,
  setTotalApplications,
  setCurrentPage,
  setSelectedAppIdName
} from '../../store/ApplicationSlice';
import MenuList from '../../components/MenuList';
import PaginationControls from './Pagination';
import { transformAppetiteData } from '../../utils/generalUtils';
import {
  fetchApplicationDataByIdAndName,
  fetchDataForMultipleSelect,
  fetchFilterTotalApplications
} from '../../api/userService';
import IntakeTable from './IntakeTable';
import { FilterAltOff } from '@mui/icons-material';
import { dateFilterData } from '../../config/constant';

// Utility to convert string to PascalCase
const toPascalCase = str =>
  str ? str.replace(/(^|_)([a-z])/g, (_, __, c) => c.toUpperCase()) : '';

const IntakeInfo = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const appetiteData = useSelector(state => state.intakeData.appetiteData);
  const selectedApplicationId = useSelector(
    state => state.intakeData.selectedAppId
  );
  const totalApplications = useSelector(
    state => state.intakeData.totalApplications
  );
  const currentPage = useSelector(state => state.intakeData.currentPage);
  const uniqueValues = useSelector(state => state.intakeData.uniqueValues);
  const selectedAppData = useSelector(
    state => state.intakeData.selectedApplicationData
  );

  const accessToken = localStorage.getItem('access_token');
  const refreshToken = localStorage.getItem('refresh_token');

  const [highlightedRow, setHighlightedRow] = useState(0);
  const [sortConfig, setSortConfig] = useState({
    column: null,
    direction: 'asc'
  });
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false); // State for loader
  const [columnFilters, setColumnFilters] = useState({});
  const [dateFilters, setDateFilters] = useState(dateFilterData);
  const itemsPerPage = 15; // Number of items per page

  // Build table data
  const tableData = useMemo(
    () => transformAppetiteData(appetiteData),
    [appetiteData]
  );

  useEffect(() => {
    if (tableData.length) {
      dispatch(setSelectedAppIdName(tableData[0]?.customer_name));
      dispatch(setAppId(tableData[0].application_id));
    }
  }, [tableData, dispatch]);

  // Sorting logic
  const sortedData = useMemo(() => {
    if (!sortConfig.column) return tableData;
    return [...tableData].sort((a, b) => {
      if (a[sortConfig.column] < b[sortConfig.column])
        return sortConfig.direction === 'asc' ? -1 : 1;
      if (a[sortConfig.column] > b[sortConfig.column])
        return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [tableData, sortConfig]);

  // Pagination Logic: Get items for the current page
  const paginatedData = useMemo(() => {
    return sortedData.slice(0, itemsPerPage);
  }, [sortedData, itemsPerPage]);

  // Handle Pagination
  const totalPages = useMemo(() => {
    if (!totalApplications || !itemsPerPage) return 1; // Default to 1 page if values are missing
    return Math.ceil(totalApplications / itemsPerPage);
  }, [totalApplications, itemsPerPage]);

  // Fetch application data on application selection change
  useEffect(() => {
    if (!selectedApplicationId) return;

    const fetchSelectedAppData = async () => {
      const customer = paginatedData.find(
        app => app.application_id === selectedApplicationId
      );
      if (customer && customer?.customer_name) {
        const response = await fetchApplicationDataByIdAndName(
          customer.customer_name,
          selectedApplicationId
        );
        dispatch(setSelectedApplicationIdData(response));
      }
    };

    fetchSelectedAppData();
  }, [selectedApplicationId, paginatedData, dispatch]);

  // Event handlers
  const handleSort = column => {
    setSortConfig(prev => ({
      column,
      direction:
        prev.column === column && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleRowClick = (index, id, name) => {
    setHighlightedRow(index);
    dispatch(setAppId(id));
    dispatch(setSelectedAppIdName(name));
    dispatch(
      selectedApplicationData(
        appetiteData.find(app => app.application_id === id)
      )
    );
  };

  const handleRowDoubleClick = id => {
    const route = `/app/intake/${id}`;
    if (location.pathname !== route) {
      dispatch(addBreadcrumb({ label: 'Application Overview', path: route }));
    }
    navigate(route);
  };

  const toggleMenu = () => setIsMenuOpen(prev => !prev);

  // Handle Pagination
  const handlePageChange = async newPage => {
    dispatch(setCurrentPage(newPage));
    await handleUpdateData(newPage);
  };

  const handleUpdateData = async newPage => {
    setIsLoading(true);
    const response = await fetchDataForMultipleSelect(
      columnFilters,
      dateFilters,
      accessToken,
      refreshToken,
      newPage
    );
    const totalCount = await fetchFilterTotalApplications(
      columnFilters,
      dateFilters,
      accessToken,
      refreshToken
    );

    if (response) {
      dispatch(setAppetiteData(response)); // Update Redux store
    }
    if (totalCount) {
      dispatch(setTotalApplications(totalCount.total));
    }
    setIsLoading(false);
  };

  const handleMailSourceFilterChange = async selectedValue => {
    setIsLoading(true);
    const filterKey = 'mail_source';
    const updatedFilters = { ...columnFilters, [filterKey]: [selectedValue] };
    setColumnFilters(updatedFilters);
    const response = await fetchDataForMultipleSelect(
      updatedFilters,
      dateFilters,
      accessToken,
      refreshToken
    );
    const totalCount = await fetchFilterTotalApplications(
      updatedFilters,
      dateFilters,
      accessToken,
      refreshToken
    );
    if (response) {
      dispatch(setAppetiteData(response));
    }
    if (totalCount) {
      dispatch(setTotalApplications(totalCount.total));
    }
    dispatch(setCurrentPage(1));
    setIsLoading(false);
  };

  return (
    <div className='mx-auto relative'>
      {/* Loader Overlay */}
      {isLoading && location.pathname !== '/' && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.4)', // Transparent grey background
            zIndex: 9999, // Ensures it overlays everything
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <CircularProgress
            size={60}
            sx={{
              color: '#ea580c'
            }}
          />
        </Box>
      )}
      {/* Header */}
      <div className='flex items-center justify-between'>
        {/* Left Side: Menu and Title */}
        <div className='flex items-center space-x-4 mb-4'>
          <MenuList isOpen={isMenuOpen} toggleMenu={toggleMenu} />
          <h1 className='py-2 text-2xl text-orange-600'>
            Intake Triage and Priority
          </h1>
        </div>
        {/* Right Side: Filters */}
        <div className='flex items-center space-x-2 mb-4 ml-auto'>
          <Select
            displayEmpty
            value={columnFilters['mail_source']?.[0] || ''}
            onChange={e => handleMailSourceFilterChange(e.target.value)}
            size='small'
            variant='outlined'
            style={{ minWidth: 200 }}
          >
            <MenuItem value=''>
              <em>All Sources</em>
            </MenuItem>
            {uniqueValues?.mail_source?.map(source => (
              <MenuItem key={source} value={source}>
                {toPascalCase(source)}
              </MenuItem>
            ))}
          </Select>
          <Tooltip title={'clear all filters'} placement='top' arrow>
            <IconButton
              onClick={() => {
                handleUpdateData(1);
                setColumnFilters({});
                dispatch(setCurrentPage(1));
              }}
              style={{ color: '#FF4D00' }}
            >
              <FilterAltOff />
            </IconButton>
          </Tooltip>
        </div>
      </div>

      {/* Table and Details Side by Side */}
      <div className='flex flex-row w-full'>
        {/* Table Wrapper */}
        <div className='flex-grow transform transition-transform duration-300 intake-table-wrapper'>
          <IntakeTable
            setIsLoading={setIsLoading}
            columnFilters={columnFilters}
            setColumnFilters={setColumnFilters}
            sortConfig={sortConfig}
            paginatedData={paginatedData}
            highlightedRow={highlightedRow}
            handleSort={handleSort}
            handleRowClick={handleRowClick}
            handleRowDoubleClick={handleRowDoubleClick}
            dateFilters={dateFilters}
            setDateFilters={setDateFilters}
          />

          {/* Pagination Controls */}
          <PaginationControls
            currentPage={currentPage}
            totalPages={totalPages}
            pageSize={itemsPerPage}
            totalItems={totalApplications}
            onPageChange={handlePageChange}
          />
        </div>
      </div>
    </div>
  );
};

export default IntakeInfo;
